/**
 * Small round play button that previews a voice by hitting
 * `POST /api/voice-sample` (auto-caches per voice_id in GridFS)
 * and playing the returned MP3 inline. Reusable across every voice
 * picker in the app.
 *
 * Props:
 *   voiceId — required. Empty string / null renders the button
 *             disabled (Auto has nothing to preview).
 *   size    — "sm" (default) | "md"
 *   testid  — data-testid prefix, defaults to "voice-sample-play"
 */
import { useEffect, useRef, useState } from "react";
import { Play, Pause, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function VoiceSamplePlayButton({
  voiceId,
  size = "sm",
  testid = "voice-sample-play",
  className = "",
}) {
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  // Cache the sample URL per voice for the component's lifetime so a
  // repeat click after the first play doesn't re-hit the endpoint.
  const urlCache = useRef({});
  const audioRef = useRef(null);
  const disabled = !voiceId;

  // Stop playback if voiceId changes underneath us.
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlaying(false);
  }, [voiceId]);

  const onClick = async () => {
    if (disabled) return;
    // Toggle pause when already playing.
    if (playing && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlaying(false);
      return;
    }
    setLoading(true);
    try {
      let url = urlCache.current[voiceId];
      if (!url) {
        const { data } = await api.post("/voice-sample", {
          voice_id: voiceId,
        }, { timeout: 30_000 });
        url = data?.url;
        if (!url) throw new Error("no_url");
        urlCache.current[voiceId] = url;
      }
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const full = url.startsWith("http") ? url : `${backend}${url}`;
      const audio = new Audio(full);
      audio.onended = () => {
        setPlaying(false);
        audioRef.current = null;
      };
      audio.onerror = () => {
        setPlaying(false);
        audioRef.current = null;
        toast.error("Couldn't play voice sample");
      };
      audioRef.current = audio;
      setPlaying(true);
      await audio.play();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
        || "Couldn't generate voice sample — try again in a moment.",
      );
    } finally {
      setLoading(false);
    }
  };

  const dim = size === "md" ? "w-9 h-9" : "w-7 h-7";
  const icon = size === "md" ? "w-4 h-4" : "w-3.5 h-3.5";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      title={
        disabled
          ? "Pick a specific voice first (Auto has no preview)"
          : playing
            ? "Stop preview"
            : "Preview this voice"
      }
      data-testid={`${testid}${voiceId ? `-${voiceId}` : ""}`}
      className={`inline-flex items-center justify-center ${dim} rounded-full transition-colors flex-shrink-0 ${
        disabled
          ? "border border-zinc-900 text-zinc-700 cursor-not-allowed"
          : playing
            ? "bg-yellow-400 text-black hover:bg-yellow-300"
            : "border border-yellow-400/50 text-yellow-300 hover:bg-yellow-400 hover:text-black"
      } ${className}`}
    >
      {loading
        ? <Loader2 className={`${icon} animate-spin`} />
        : playing
          ? <Pause className={icon} />
          : <Play className={`${icon} ml-[1px]`} />}
    </button>
  );
}
